import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mui/material';

import { FeedKey, SortMode } from 'api';
import { FeedViewOptions } from 'types/app';
import { EmptyState, Layouts } from 'components';
import { FeedDuplicatesProvider } from 'providers';
import {
  DEFAULT_SORT,
  FEED_LIMIT_LIST,
  FOLLOW_QUERIES_LIMIT,
  LOCAL_STORAGE_KEY_FEED_SORT,
  LOCAL_STORAGE_KEY_FEED_VIEW,
} from 'app-constants';
import {
  useCurrentUser,
  useFollowedAllResultsQuery,
  useProtectedClient,
  useUserPreferenceStorage,
} from 'hooks';
import { FeedList, FeedSelect } from 'features/feed';
import { SetupProfileButton, SetupWorkspaceButton } from 'features/setup';
import * as Links from 'Links';

import { GridTimelineLayout } from './containers';
import { FeedMenu, Header, RightSidebar, Welcome } from './components';

const TimelineIndex: FC = () => {
  const [view, setView] = useUserPreferenceStorage(
    LOCAL_STORAGE_KEY_FEED_VIEW,
    FeedViewOptions.List
  );
  const [sortChoice, setSortChoice] = useUserPreferenceStorage(
    LOCAL_STORAGE_KEY_FEED_SORT,
    DEFAULT_SORT
  );

  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const client = useProtectedClient();

  // Refetch group follows to ensure list is current
  useFollowedAllResultsQuery({
    data: { user_id: currentUser.id, limit: FOLLOW_QUERIES_LIMIT },
  });

  const sort: boolean = client.sort_mode ? client.sort_mode === SortMode.Trending : sortChoice;

  function handleSort() {
    setSortChoice(!sort);
  }

  function handleViewChange() {
    setView(view === FeedViewOptions.List ? FeedViewOptions.Grid : FeedViewOptions.List);
  }

  function renderEmptyState() {
    return (
      <EmptyState
        box
        name="timeline"
        actionComponent={
          <Box paddingTop={2}>
            <Button component={Links.PostCreate} color="primary" variant="contained" size="large">
              {t('emptyStates:timelineActionText')}
            </Button>
          </Box>
        }
      />
    );
  }

  function renderLeftSidebar() {
    return (
      <FeedSelect menu={client.sort_mode ? null : <FeedMenu sort={sort} onSort={handleSort} />}>
        <SetupWorkspaceButton />
        <SetupProfileButton />
      </FeedSelect>
    );
  }

  function renderMainGrid() {
    return (
      <FeedDuplicatesProvider>
        <GridTimelineLayout
          sort={sort}
          emptyState={renderEmptyState()}
          header={<Header activeView={view} onChange={handleViewChange} />}
        />
      </FeedDuplicatesProvider>
    );
  }

  function renderMainList() {
    const listVariables = {
      sort,
      key: FeedKey.Timeline,
      id: currentUser.id,
      limit: FEED_LIMIT_LIST,
    };

    return (
      <Layouts.AppMain2Column
        desktopSplit="8/4"
        headerSpacing={1}
        mainSpacing={2}
        header={<Header activeView={view} onChange={handleViewChange} />}
        left={
          <FeedList
            emptyState={renderEmptyState}
            variables={listVariables}
            selectedGroup={null}
            header={<Welcome />}
          />
        }
        right={<RightSidebar showInvite={client.allow_user_invites} variant="secondary" />}
      />
    );
  }

  if (view === FeedViewOptions.Grid) {
    return (
      <Layouts.App2Column
        desktopSplit="2/10"
        mainWidth="wide"
        sidebar={renderLeftSidebar()}
        main={renderMainGrid()}
      />
    );
  }

  if (view === FeedViewOptions.List) {
    return (
      <Layouts.App2Column
        mainWidth="standard"
        sidebar={renderLeftSidebar()}
        main={renderMainList()}
      />
    );
  }

  return null;
};

export default TimelineIndex;
